import {
  browserTracingIntegration,
  makeBrowserOfflineTransport,
  makeFetchTransport,
  replayIntegration,
  type BrowserOptions,
  type ErrorEvent,
  type EventHint,
} from '@sentry/react';
import { getCurrentChannelSlug } from '../channel';
import { applyHttpErrorToEvent, createHttpErrorPreprocessIntegration, isHttpError } from './http-error-event';
import { SENTRY_SESSION_STORAGE_KEYS } from './sentry-session-storage-keys';
import { tagThirdPartySpan, tagThirdPartyBreadcrumb, tagThirdPartyEvent } from './third-party-tagger';

type SentryEnvironment = 'test' | 'preview' | 'production' | 'debug';

/**
 * Global error patterns that always apply to all apps.
 * App-specific patterns (via overrides) are merged with these.
 */
const GLOBAL_IGNORE_ERRORS: (string | RegExp)[] = [];

/**
 * Global deny URL patterns that always apply to all apps.
 * App-specific patterns (via overrides) are merged with these.
 */
const GLOBAL_DENY_URLS: RegExp[] = [];

type RequiredSentryConfig = {
  dsn: string;
  environment: SentryEnvironment;
  release: string | undefined;
};

export type SentryConfigOverrides = Partial<Omit<BrowserOptions, 'dsn' | 'environment' | 'release'>> & {
  disableTransport?: boolean;
  disableReplay?: boolean;
  /** Callback invoked at event-send time; returned key-value pairs are merged into the event's tags. */
  extraTags?: () => Record<string, string | number | boolean>;
};

export type SentryConfigInput = RequiredSentryConfig & SentryConfigOverrides;

const hasOwn = <K extends PropertyKey>(obj: object, key: K): obj is Record<K, unknown> =>
  Object.prototype.hasOwnProperty.call(obj, key);

const getSessionStorageItem = (key: string): string | null => {
  try {
    if (typeof globalThis === 'undefined' || !('sessionStorage' in globalThis)) {
      return null;
    }

    return globalThis.sessionStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

const parseSessionStorageSampleRate = (key: string): number | undefined => {
  const value = getSessionStorageItem(key);
  if (value === null) {
    return undefined;
  }

  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 1) {
    return undefined;
  }

  return parsed;
};

const parseSessionStorageBoolean = (key: string): boolean | undefined => {
  const value = getSessionStorageItem(key);
  if (value === null) {
    return undefined;
  }

  return value === 'true';
};

// Matches the wording used by Safari, Chrome/Edge, and Firefox when a dynamic import fails to load its chunk.
export const DYNAMIC_IMPORT_ERROR_FRAGMENTS = [
  'Importing a module script failed',
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
] as const;

const isDynamicImportError = (event: ErrorEvent): boolean => {
  const messages = [event.message, ...(event.exception?.values?.map((value) => value.value) ?? [])];
  return messages.some(
    (m) =>
      typeof m === 'string' &&
      DYNAMIC_IMPORT_ERROR_FRAGMENTS.some((fragment) => m.toLowerCase().includes(fragment.toLowerCase()))
  );
};

/**
 * Returns a base Sentry config with sensible defaults for browser apps.
 *
 * Core fields (`dsn`, `environment`, and `release`) are required.
 *
 * Any field in `overrides` fully replaces the corresponding default unless noted otherwise.
 * Filtering lists are merged with global lists so global filtering always applies.
 *
 * @example
 * sentry.init(
 *   getSentryConfig({
 *     dsn: appConfig.SENTRY_DSN,
 *     environment: 'production',
 *     release: appConfig.VERSION,
 *   })
 * );
 *
 * @example
 * // Disable replay and offline transport defaults explicitly
 * sentry.init(
 *   getSentryConfig({
 *     dsn: appConfig.SENTRY_DSN,
 *     environment: 'test',
 *     release: appConfig.VERSION,
 *     disableReplay: true,
 *     disableTransport: true,
 *   })
 * );
 */
export function getSentryConfig({ dsn, environment, release, ...overrides }: SentryConfigInput): BrowserOptions {
  const disableTransport = overrides.disableTransport === true;
  const disableReplay = overrides.disableReplay === true;
  const hasExplicitIntegrations = hasOwn(overrides, 'integrations');

  const defaultSampleRate = environment === 'production' ? 0.25 : 0;
  const defaultTraceSampleRate = environment === 'production' ? 0.01 : 0;
  const sessionStorageErrorSampleRate = parseSessionStorageSampleRate(SENTRY_SESSION_STORAGE_KEYS.errorSampleRate);
  const sessionStorageTraceSampleRate = parseSessionStorageSampleRate(SENTRY_SESSION_STORAGE_KEYS.traceSampleRate);
  const sessionStorageDebug = parseSessionStorageBoolean(SENTRY_SESSION_STORAGE_KEYS.debug);

  // SessionStorage overrides are intentionally highest precedence for local debugging/validation.
  const effectiveErrorSampleRate = sessionStorageErrorSampleRate ?? overrides?.sampleRate ?? defaultSampleRate;
  const effectiveTraceSampleRate =
    sessionStorageTraceSampleRate ?? overrides?.tracesSampleRate ?? defaultTraceSampleRate;

  const defaultIntegrations = hasExplicitIntegrations
    ? undefined
    : [
        createHttpErrorPreprocessIntegration(),
        browserTracingIntegration({ instrumentNavigation: true, enableInp: true }),
        ...(disableReplay ? [] : [replayIntegration({ stickySession: true })]),
      ];

  const config: BrowserOptions = {
    dsn,
    environment,
    release,
    debug: sessionStorageDebug ?? overrides?.debug,
    attachStacktrace: true,

    // Sampling
    sampleRate: effectiveErrorSampleRate,
    tracesSampleRate: effectiveTraceSampleRate,
    replaysSessionSampleRate: overrides?.replaysSessionSampleRate ?? 0,
    replaysOnErrorSampleRate:
      overrides?.replaysOnErrorSampleRate ?? (disableReplay ? 0 : environment === 'production' ? 0.25 : 0),

    // Filtering — merge global patterns with app-specific overrides
    ignoreErrors: overrides?.ignoreErrors ? [...GLOBAL_IGNORE_ERRORS, ...overrides.ignoreErrors] : GLOBAL_IGNORE_ERRORS,
    ignoreTransactions: overrides?.ignoreTransactions
      ? [...GLOBAL_IGNORE_ERRORS, ...overrides.ignoreTransactions]
      : GLOBAL_IGNORE_ERRORS,
    denyUrls: overrides?.denyUrls ? [...GLOBAL_DENY_URLS, ...overrides.denyUrls] : GLOBAL_DENY_URLS,

    integrations: hasExplicitIntegrations ? overrides.integrations : defaultIntegrations,

    beforeBreadcrumb: overrides?.beforeBreadcrumb ?? ((breadcrumb) => tagThirdPartyBreadcrumb(breadcrumb)),

    beforeSendSpan:
      overrides?.beforeSendSpan ??
      ((span) => {
        const taggedSpan = tagThirdPartySpan(span);
        // Drop noisy Pendo static resource spans
        if (span?.op === 'resource.script' && span?.description?.includes('pendo-static-')) {
          return null as any; // Sentry types incorrectly omit null as a valid return value
        }
        return taggedSpan;
      }),

    beforeSend:
      overrides?.beforeSend ??
      ((event: ErrorEvent, hint: EventHint) => {
        let taggedEvent = tagThirdPartyEvent(event);

        if (isHttpError(hint.originalException)) {
          taggedEvent = applyHttpErrorToEvent(taggedEvent, hint.originalException, {
            team: typeof taggedEvent.tags?.team === 'string' ? taggedEvent.tags.team : undefined,
            topic: typeof taggedEvent.tags?.topic === 'string' ? taggedEvent.tags.topic : undefined,
          });
        }

        const channel = getCurrentChannelSlug() ?? 'unknown';
        taggedEvent.tags = {
          ...taggedEvent.tags,
          channel,
          'commit-hash': import.meta.env?.VITE_WEAVE_COMMITHASH ?? '',
          version: import.meta.env?.VITE_WEAVE_VERSION ?? '',
          ...(overrides.extraTags ? overrides.extraTags() : {}),
        };

        // Dynamic-import / chunk-load failures are typically transient network issues; keep visibility but don't trip fatal/error alerts.
        if (isDynamicImportError(taggedEvent)) {
          taggedEvent.level = 'warning';
        }

        // Allow individual events to carry their own sample rate via event.contexts.meta.sampleRate,
        // falling back to the environment default.
        const eventSampleRate =
          environment === 'production' && typeof taggedEvent?.contexts?.meta?.sampleRate === 'number'
            ? taggedEvent.contexts.meta.sampleRate
            : undefined;

        const sampleRate = sessionStorageErrorSampleRate ?? eventSampleRate ?? effectiveErrorSampleRate;
        return Math.random() < sampleRate ? taggedEvent : null;
      }),
  };

  // Allow explicitly disabling default transport behavior.
  if (hasOwn(overrides, 'transport')) {
    config.transport = overrides.transport;
  } else if (!disableTransport) {
    config.transport = makeBrowserOfflineTransport(makeFetchTransport);
  }

  // Keep queue settings only when transport is enabled unless explicitly overridden.
  if (hasOwn(overrides, 'transportOptions')) {
    config.transportOptions = overrides.transportOptions;
  } else if (!disableTransport) {
    // @ts-expect-error maxQueueSize is valid at runtime but missing from Sentry's type declaration
    config.transportOptions = { maxQueueSize: 10 };
  }

  return config;
}
